import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, ArrowUp, ArrowDown, Loader2, RefreshCw, MapPin, Package, HelpCircle, MessageSquare, Layers, Award, GripVertical } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useToast } from '../../hooks/use-toast';
import {
  europeBranches as defEU,
  sriLankaBranches as defSL,
  otherServices as defOther,
  services as defServices,
  industries as defIndustries,
  faqs as defFaqs,
  testimonials as defTestimonials,
  stats as defStats,
} from '../../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available icon names admin can pick from (must match lucide-react components used elsewhere)
const ICONS = ['Plane','Ship','Truck','Warehouse','ShieldCheck','ShieldAlert','FileCheck','PackageOpen','TruckIcon','Home','Gift','Headphones','PhoneCall','Car','Cpu','HeartPulse','Leaf','Factory','ShoppingBag'];

// Schema definitions for each content type
const SCHEMAS = {
  branches_eu: {
    label: 'Europe Branches',
    icon: MapPin,
    key: 'branches_eu',
    defaults: defEU,
    fields: [
      { name: 'name', label: 'Branch Name', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'city', label: 'City', type: 'text', width: 'w-full md:w-40' },
      { name: 'country', label: 'Country', type: 'text', width: 'w-full md:w-36' },
      { name: 'phone', label: 'Phone', type: 'text', required: true, width: 'w-full md:w-44' },
      { name: 'whatsapp', label: 'WhatsApp Number', type: 'text', width: 'w-full md:w-44' },
      { name: 'staff_email', label: 'Staff Email (in-charge)', type: 'text', width: 'w-full md:w-56' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'lat', label: 'Latitude', type: 'number', width: 'w-full md:w-28' },
      { name: 'lng', label: 'Longitude', type: 'number', width: 'w-full md:w-28' },
      { name: 'head', label: 'Mark as Head Office', type: 'boolean' },
    ],
  },
  branches_sl: {
    label: 'Sri Lanka Branches',
    icon: MapPin,
    key: 'branches_sl',
    defaults: defSL,
    fields: [
      { name: 'name', label: 'Branch Name', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'phone', label: 'Phone', type: 'text', required: true, width: 'w-full md:w-44' },
      { name: 'whatsapp', label: 'WhatsApp Number', type: 'text', width: 'w-full md:w-44' },
      { name: 'staff_email', label: 'Staff Email (in-charge)', type: 'text', width: 'w-full md:w-56' },
      { name: 'lat', label: 'Latitude', type: 'number', width: 'w-full md:w-28' },
      { name: 'lng', label: 'Longitude', type: 'number', width: 'w-full md:w-28' },
    ],
  },
  other_services: {
    label: 'Other Services',
    icon: Package,
    key: 'other_services',
    defaults: defOther,
    fields: [
      { name: 'name', label: 'Service Name', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'region', label: 'Region', type: 'text', width: 'w-full md:w-40' },
      { name: 'phone', label: 'Phone', type: 'text', required: true, width: 'w-full md:w-44' },
      { name: 'icon', label: 'Icon', type: 'select', options: ICONS, width: 'w-full md:w-40' },
      { name: 'desc', label: 'Description', type: 'textarea' },
    ],
  },
  services: {
    label: 'Services',
    icon: Layers,
    key: 'services',
    defaults: defServices,
    fields: [
      { name: 'title', label: 'Title', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'icon', label: 'Icon', type: 'select', options: ICONS, width: 'w-full md:w-40' },
      { name: 'desc', label: 'Description', type: 'textarea', required: true },
    ],
  },
  industries: {
    label: 'Industries',
    icon: Award,
    key: 'industries',
    defaults: defIndustries,
    fields: [
      { name: 'name', label: 'Industry', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'icon', label: 'Icon', type: 'select', options: ICONS, width: 'w-full md:w-40' },
    ],
  },
  faqs: {
    label: 'FAQs',
    icon: HelpCircle,
    key: 'faqs',
    defaults: defFaqs,
    fields: [
      { name: 'q', label: 'Question', type: 'text', required: true },
      { name: 'a', label: 'Answer', type: 'textarea', required: true },
    ],
  },
  testimonials: {
    label: 'Testimonials',
    icon: MessageSquare,
    key: 'testimonials',
    defaults: defTestimonials,
    fields: [
      { name: 'name', label: 'Customer Name', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'location', label: 'Location', type: 'text', width: 'w-full md:w-56' },
      { name: 'text', label: 'Quote', type: 'textarea', required: true },
    ],
  },
  stats: {
    label: 'Stats',
    icon: Award,
    key: 'stats',
    defaults: defStats,
    fields: [
      { name: 'label', label: 'Label', type: 'text', required: true, width: 'w-full md:w-64' },
      { name: 'value', label: 'Value', type: 'number', required: true, width: 'w-full md:w-28' },
      { name: 'suffix', label: 'Suffix', type: 'text', width: 'w-full md:w-28' },
    ],
  },
};

const Row = ({ item, index, fields, onChange, onRemove, onUp, onDown, canUp, canDown }) => (
  <div className="bg-white border border-neutral-200 p-5 mb-3">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-neutral-300" />
        <span className="font-serif-display font-bold text-black">#{index + 1}</span>
        <span className="text-xs text-neutral-500 truncate max-w-[300px]">{item.name || item.title || item.q || item.label || 'New entry'}</span>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" disabled={!canUp} onClick={onUp} className="w-8 h-8 flex items-center justify-center border border-neutral-200 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" disabled={!canDown} onClick={onDown} className="w-8 h-8 flex items-center justify-center border border-neutral-200 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ArrowDown className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={onRemove} className="w-8 h-8 flex items-center justify-center border border-neutral-200 hover:bg-[#c8102e] hover:border-[#c8102e] hover:text-white transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
    <div className="flex flex-wrap gap-3">
      {fields.map((f) => (
        <div key={f.name} className={f.width || 'w-full'}>
          <label className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-semibold mb-1.5">{f.label}{f.required && <span className="text-[#c8102e]">*</span>}</label>
          {f.type === 'textarea' ? (
            <Textarea value={item[f.name] || ''} onChange={(e) => onChange(f.name, e.target.value)} rows={2} className="text-sm" />
          ) : f.type === 'boolean' ? (
            <label className="inline-flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={!!item[f.name]} onChange={(e) => onChange(f.name, e.target.checked)} className="w-4 h-4 accent-[#c8102e]" /> {f.label}</label>
          ) : f.type === 'select' ? (
            <select value={item[f.name] || ''} onChange={(e) => onChange(f.name, e.target.value)} className="w-full h-11 border border-neutral-300 px-3 text-sm bg-white">
              <option value="">— select —</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input type={f.type === 'number' ? 'number' : 'text'} step={f.type === 'number' ? 'any' : undefined} value={item[f.name] ?? ''} onChange={(e) => onChange(f.name, f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)} className="h-11 text-sm" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const ContentEditor = ({ schemaKey }) => {
  const schema = SCHEMAS[schemaKey];
  const { token } = useAuth();
  const { content, refresh } = useSiteSettings();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // If there's admin content already, use it; else fall back to defaults so the admin can start from something.
    const existing = content[schema.key];
    if (Array.isArray(existing) && existing.length > 0) setItems(existing);
    else setItems([...(schema.defaults || [])]);
  }, [schemaKey, content, schema]);

  const patch = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const add = () => setItems((arr) => [...arr, {}]);
  const remove = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const move = (idx, dir) => setItems((arr) => {
    const next = [...arr]; const to = idx + dir;
    if (to < 0 || to >= next.length) return next;
    [next[idx], next[to]] = [next[to], next[idx]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${schema.key}`, { items }, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast({ title: 'Saved', description: `${schema.label} updated on the live site.` });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally { setSaving(false); }
  };

  const resetToDefaults = async () => {
    if (!window.confirm(`Reset ${schema.label} to the original defaults? This deletes your custom edits.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/content/${schema.key}`, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      setItems([...(schema.defaults || [])]);
      toast({ title: 'Reset done', description: `${schema.label} restored to defaults.` });
    } catch (e) {
      toast({ title: 'Reset failed' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 pb-4 border-b border-neutral-200">
        <div>
          <div className="font-serif-display text-2xl font-bold text-black">{schema.label}</div>
          <div className="text-xs text-neutral-500 mt-1">{items.length} {items.length === 1 ? 'entry' : 'entries'} — reorder with arrows, add via the button below.</div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={resetToDefaults} disabled={saving} className="inline-flex items-center gap-2 border border-neutral-200 hover:border-black hover:bg-white h-11 px-4 text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
          <Button onClick={save} disabled={saving} className="bg-[#c8102e] hover:bg-black text-white h-11 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save {schema.label}
          </Button>
        </div>
      </div>
      {items.map((it, i) => (
        <Row key={i} item={it} index={i} fields={schema.fields}
          onChange={(k, v) => patch(i, k, v)} onRemove={() => remove(i)}
          onUp={() => move(i, -1)} onDown={() => move(i, 1)}
          canUp={i > 0} canDown={i < items.length - 1}
        />
      ))}
      <button type="button" onClick={add} className="w-full py-4 border-2 border-dashed border-neutral-300 hover:border-[#c8102e] hover:text-[#c8102e] text-neutral-500 flex items-center justify-center gap-2 text-sm font-semibold transition-colors">
        <Plus className="w-4 h-4" /> Add new entry
      </button>
    </div>
  );
};

const ContentManager = () => {
  const [active, setActive] = useState('branches_eu');
  const tabs = Object.values(SCHEMAS);
  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Content Manager</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Edit anything on the site</h1>
        <p className="text-sm text-neutral-500 mt-2">Add, remove or edit any content block. Changes are live the moment you save.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 pb-6 border-b border-neutral-200">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button key={t.key} onClick={() => setActive(t.key)} className={`inline-flex items-center gap-2 px-4 py-2.5 text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors ${isActive ? 'bg-black text-white' : 'bg-white border border-neutral-200 text-black hover:border-black'}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      <ContentEditor key={active} schemaKey={active} />
    </AdminLayout>
  );
};

export default ContentManager;
